import { FC, useMemo, useRef, useState } from "react";
import {
  DataGridPro as DataGrid,
  GridColDef,
  GridFilterModel,
  GridLoadingOverlay,
  GridLogicOperator,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { Alert, Box, Button, Paper, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ActionButton from "src/Pages/Company/Absences/ActionButton/ActionButton";
import { format } from "date-fns";
import { pl } from "date-fns/locale/pl";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Link, useLocation } from "react-router-dom";
import __ from "src/utils/Translations";
import { useTableOperators } from "src/utils/hooks/absences/useTableOperators";
import { useLocalizationsList } from "src/Pages/Company/OrganizationStructure/api/useLocalizationsList";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import {
  organizationStructureLocalizationEdit,
  organizationStructureLocalizationsExport,
  organizationStructureLocalizationsImport,
} from "src/utils/RoleBasedSecurity/permissions";
import { buildRsqlFilters } from "src/Pages/Company/OrganizationStructure/utils/buildRsqlFilters";
import {
  EditLocalizationResponse,
  Localization,
} from "src/Pages/Company/OrganizationStructure/Localizations/localizations.models";
import EditLocalizationModal from "src/Pages/Company/OrganizationStructure/Localizations/Modal/EditLocalizationModal";
import DeleteLocalizationModal from "src/Pages/Company/OrganizationStructure/Localizations/Modal/DeleteLocalizationModal";
import DateRangeFilter from "src/Pages/Company/EmployeeManagement/SyncErrors/Table/DateRangeFilter";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import FEATURE_FLAGS from "src/utils/constants/featureFlags";
import NumericFilter from "./NumericFilter";
import DataGridToolbar, {
  TableSettings,
} from "src/Pages/Company/OrganizationStructure/sharedComponents/DataGridToolbar/DataGridToolbar";

interface LocalizationsTableProps {
  companyId: string;
  structureId: string;
  onAddClick?: () => void;
}

const LocalizationsTable: FC<LocalizationsTableProps> = ({
  companyId,
  structureId,
  onAddClick,
}) => {
  const { getStringOperators } = useTableOperators();
  const location = useLocation();
  const hasPermissionLocalizationEdit = usePermission(
    organizationStructureLocalizationEdit
  );
  const hasExportLocalizationsEnabled = usePermission(
    organizationStructureLocalizationsExport
  );
  const hasImportLocalizationsEnabled = usePermission(
    organizationStructureLocalizationsImport
  );
  const hasImportExportLocalizationsTabEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_LOCALIZATIONS_IMPORT_EXPORT_TAB
  );

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    logicOperator: GridLogicOperator.And,
  });
  const [settings, setSettings] = useState<TableSettings>({
    density: "standard",
  });
  const [showFilters, setShowFilters] = useState(true);
  const [editingLocalization, setEditingLocalization] =
    useState<EditLocalizationResponse | null>(null);
  const [deletingLocalization, setDeletingLocalization] =
    useState<Localization | null>(null);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const requestParams = useMemo(
    () => ({
      companyId,
      structureId,
      page: paginationModel.page,
      size: paginationModel.pageSize,
      sort:
        sortModel.map(({ field, sort }) => `${field}:${sort}`).join(",") ||
        undefined,
      filters: buildRsqlFilters(
        filterModel,
        ["createdAt"],
        ["employeesNumber"]
      ),
    }),
    [companyId, structureId, paginationModel, sortModel, filterModel]
  );

  const { data, isFetching, isError, error } =
    useLocalizationsList(requestParams);

  const rowCountRef = useRef(data?.totalElements ?? 0);
  const rowCount = useMemo(() => {
    if (data?.totalElements !== undefined) {
      rowCountRef.current = data.totalElements;
    }
    return rowCountRef.current;
  }, [data?.totalElements]);

  const columns: GridColDef<Localization>[] = useMemo(() => {
    const base: GridColDef<Localization>[] = [
      {
        field: "city",
        headerName: __("Miasto"),
        flex: 1,
        filterOperators: getStringOperators({ labelUT: "Podaj miasto" }),
        valueFormatter: (value: string | null) => value ?? "-",
      },
      {
        field: "streetAddress",
        headerName: __("Adres"),
        flex: 1,
        filterOperators: getStringOperators({ labelUT: "Podaj adres" }),
        valueFormatter: (value: string | null) => value ?? "-",
      },
      {
        field: "postalCode",
        headerName: __("Kod pocztowy"),
        flex: 1,
        filterOperators: getStringOperators({
          labelUT: "Podaj kod pocztowy",
        }),
        valueFormatter: (value: string | null) => value ?? "-",
      },
      {
        field: "createdAt",
        headerName: __("Data dodania"),
        flex: 1,
        renderHeaderFilter: (params) => <DateRangeFilter {...params} />,
        valueFormatter: (value: string) =>
          value ? format(new Date(value), "dd.MM.yyyy") : "-",
      },
      {
        field: "employeesNumber",
        headerName: __("Liczba pracowników"),
        flex: 1,
        type: "number",
        align: "left",
        headerAlign: "left",
        renderHeaderFilter: (params) => <NumericFilter {...params} />,
      },
    ];

    if (hasPermissionLocalizationEdit) {
      base.push({
        field: "actions",
        type: "actions",
        headerName: __("Akcje"),
        flex: 1,
        renderCell: (params) => (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <ActionButton
              label={__("Edytuj")}
              bgColor="primary.main"
              bgColorHover="primary.dark"
              color="white"
              onClick={() =>
                setEditingLocalization({
                  id: params.row.id,
                  city: params.row.city,
                  postalCode: params.row.postalCode,
                  streetAddress: params.row.streetAddress,
                })
              }
              dataTestId={`os__localizations-edit-button-${params.row.id}`}
            >
              <EditIcon />
            </ActionButton>
            <ActionButton
              label={__("Usuń")}
              bgColor="error.main"
              bgColorHover="error.dark"
              color="white"
              onClick={() => setDeletingLocalization(params.row)}
              dataTestId={`os__localizations-delete-button-${params.row.id}`}
            >
              <DeleteIcon />
            </ActionButton>
          </Stack>
        ),
      });
    }

    return base;
  }, [getStringOperators, hasPermissionLocalizationEdit]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      <Paper data-testid="localizations-table" sx={{ pt: 2, pb: 1, mb: 2 }}>
        <Box sx={{ height: 770, width: "100%" }}>
          {isError && (
            <Alert variant="outlined" color="warning">
              {String(error)}
            </Alert>
          )}
          <DataGrid
            columns={columns}
            rows={data?.content ?? []}
            rowCount={rowCount}
            loading={isFetching}
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            disableEval
            showCellVerticalBorder={false}
            showColumnVerticalBorder={false}
            density={settings.density}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            disableColumnFilter
            headerFilters={showFilters}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            filterDebounceMs={500}
            showToolbar
            slots={{
              headerFilterMenu: null,
              toolbar: () => (
                <DataGridToolbar
                  id="localizations"
                  settings={settings}
                  setSettings={setSettings}
                  showFilters={showFilters}
                  setShowFilters={setShowFilters}
                  actionButton={
                    <>
                      {hasImportExportLocalizationsTabEnabled &&
                        (hasImportLocalizationsEnabled ||
                          hasExportLocalizationsEnabled) && (
                          <Button
                            variant="outlined"
                            component={Link}
                            to={`${location.pathname}/import-export-localizations`}
                            data-testid="os__localizations-import-export-button"
                            sx={{
                              mr: 1,
                              px: "16px",
                              py: "6px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {__("Importuj / eksportuj")}
                          </Button>
                        )}
                      {onAddClick && (
                        <Button
                          variant="contained"
                          onClick={onAddClick}
                          data-testid="os__localizations-add-button"
                          sx={{
                            mr: 1,
                            px: "16px",
                            py: "6px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {__("Dodaj lokalizację")}
                        </Button>
                      )}
                    </>
                  }
                />
              ),
              loadingOverlay: () => (
                <GridLoadingOverlay
                  variant="linear-progress"
                  noRowsVariant="skeleton"
                />
              ),
            }}
            slotProps={{
              row: { "data-testid": "localizations-table__row" },
              cell: { "data-testid": "localizations-table__cell" },
            }}
            localeText={{
              noRowsLabel: __("Brak lokalizacji"),
            }}
          />
        </Box>

        {editingLocalization && (
          <EditLocalizationModal
            companyId={companyId}
            structureId={structureId}
            mode="edit"
            data={editingLocalization}
            onClose={() => setEditingLocalization(null)}
          />
        )}
        {deletingLocalization && (
          <DeleteLocalizationModal
            companyId={companyId}
            structureId={structureId}
            localization={deletingLocalization}
            onClose={() => setDeletingLocalization(null)}
          />
        )}
      </Paper>
    </LocalizationProvider>
  );
};

export default LocalizationsTable;
